import type { Note } from './types'

const ORDER_STEP = 1024
const MIN_GAP = 0.01

export function compareNotesByOrder(a: Note, b: Note) {
  return Number(b.isPinned) - Number(a.isPinned)
    || a.sortOrder - b.sortOrder
    || b.updatedAt.localeCompare(a.updatedAt)
    || a.id.localeCompare(b.id)
}

export function neighborsAfterMove<T extends Pick<Note, 'id'>>(notes: T[], movedId: string, destinationIndex: number) {
  const sourceIndex = notes.findIndex((note) => note.id === movedId)
  if (sourceIndex < 0 || destinationIndex < 0 || destinationIndex >= notes.length || sourceIndex === destinationIndex) return null
  const reordered = [...notes]
  const [moved] = reordered.splice(sourceIndex, 1)
  reordered.splice(destinationIndex, 0, moved)
  return { beforeId: reordered[destinationIndex - 1]?.id ?? null, afterId: reordered[destinationIndex + 1]?.id ?? null }
}

export function planNoteReorder(notes: Note[], movedId: string, beforeId: string | null, afterId: string | null) {
  const moved = notes.find((note) => note.id === movedId)
  const before = beforeId ? notes.find((note) => note.id === beforeId) : undefined
  const after = afterId ? notes.find((note) => note.id === afterId) : undefined
  if (!moved || (!before && !after) || (beforeId && !before) || (afterId && !after)
    || beforeId === movedId || afterId === movedId
    || (before && before.isPinned !== moved.isPinned)
    || (after && after.isPinned !== moved.isPinned)) return []

  let sortOrder: number | undefined
  if (before && after) {
    if (after.sortOrder - before.sortOrder > MIN_GAP) sortOrder = (before.sortOrder + after.sortOrder) / 2
  } else if (before) sortOrder = before.sortOrder + ORDER_STEP
  else if (after) sortOrder = after.sortOrder - ORDER_STEP
  if (sortOrder !== undefined && Number.isFinite(sortOrder)) return [{ id: movedId, sortOrder }]

  // Extremely close or tied ranks need a one-time re-spacing of this pin group.
  const group = notes.filter((note) => note.isPinned === moved.isPinned).sort(compareNotesByOrder).filter((note) => note.id !== movedId)
  const insertAt = after ? group.findIndex((note) => note.id === after.id) : group.findIndex((note) => note.id === before?.id) + 1
  group.splice(insertAt, 0, moved)
  return group.map((note, index) => ({ id: note.id, sortOrder: index * ORDER_STEP }))
    .filter(({ id, sortOrder: next }) => notes.find((note) => note.id === id)?.sortOrder !== next)
}
